// ── analytics.js ──────────────────────────────────────────────────────────────
// First-party, fire-and-forget launch analytics. logEvent() records a named funnel
// event through the log_event() RPC (SECURITY DEFINER, so it works pre-auth too and
// respects the project-wide anon-write revoke). It NEVER throws and NEVER blocks the
// UI — a failed or blocked write is swallowed. No third-party trackers; the only
// identity is the session user_id the RPC stamps server-side (null when logged out).
//
// Funnel we care about for launch:
//   landing_view → signup_link_sent → (waitlist_join) → import_started →
//   import_done(+count) → first_follow
import { supabase } from './supabase'

// ── First-touch attribution ─────────────────────────────────────────────────
// So we can answer "which of the threads/posts we shared actually converts?" we
// stamp every event with a `ref` source. First-touch wins and is persisted, so a
// visitor who lands via ?ref=reddit-cordcutters and signs up 3 sessions later is
// still credited to that thread across the whole funnel.
//   • ?ref=<tag> or ?utm_source=<tag>  → that tag (tag your shared links!)
//   • ?gclid= / ?msclkid=               → "google-ads" / "bing-ads". Auto-tagging
//       appends ONLY the click id, no utm_source, so before this every paid click
//       landed in the "direct" bucket and the Aug-2026 Google Ads test was
//       unmeasurable against 247 organic-direct visitors. Never infer paid from the
//       google.com referrer — organic search sends that too.
//   • otherwise the referring domain    → e.g. "reddit.com", "producthunt.com"
//   • nothing / own domain              → "direct" (not persisted, so a later
//                                          tagged visit can still claim first touch)
// utm_campaign rides along as `camp` so several campaigns can share one ref.
const ATTR_KEY = 'nextep_attribution'
const VISITOR_KEY = 'nextep_vid'

// ── Anonymous visitor id ─────────────────────────────────────────────────────
// The ONE thing Plausible gave us that first-party events didn't: a count of unique
// VISITORS, not just events and known accounts. A random id in localStorage, stamped on
// every event, closes that gap for free (2026-07-31, replacing Plausible).
//
// Deliberately NOT a fingerprint and NOT cross-site: a random value we generate, scoped to
// this origin, cleared whenever the user clears site data. It says "these 40 events came
// from one browser", nothing more — no identity, no tracking anyone anywhere else. That's
// the whole reason we're not reaching for a third-party tracker.
export function visitorId() {
  try {
    let v = localStorage.getItem(VISITOR_KEY)
    if (!v) {
      v = (crypto?.randomUUID?.() || String(Math.random()).slice(2) + Date.now().toString(36)).slice(0, 36)
      localStorage.setItem(VISITOR_KEY, v)
    }
    return v
  } catch { return null }   // storage blocked — events still log, just without a visitor
}

export function getAttribution() {
  try { const s = localStorage.getItem(ATTR_KEY); if (s) return JSON.parse(s) } catch { /* storage blocked */ }
  let attr = { ref: 'direct' }
  try {
    const p   = new URLSearchParams(window.location.search)
    const tag = (p.get('ref') || p.get('utm_source') || '').trim()
      || (p.get('gclid')   ? 'google-ads' : '')
      || (p.get('msclkid') ? 'bing-ads'   : '')
    const camp = (p.get('utm_campaign') || '').trim()
    if (tag) {
      attr = { ref: tag.slice(0, 60), ...(camp ? { camp: camp.slice(0, 40) } : {}) }
    } else if (document.referrer) {
      const host = new URL(document.referrer).hostname.replace(/^www\./, '')
      // Ignore our own domains + localhost so in-app navigation never counts as a source.
      if (host && !/(^|\.)nextep\.show$/.test(host) && !/^(localhost|127\.0\.0\.1)$/.test(host)) attr = { ref: host }
    }
  } catch { /* URL/referrer parsing failed, stay 'direct' */ }
  if (attr.ref !== 'direct') { try { localStorage.setItem(ATTR_KEY, JSON.stringify(attr)) } catch { /* ignore */ } }
  return attr
}

// Viewport bucket, stamped on every event so we can answer "is there real desktop
// traffic worth building for" without a dedicated tracker (Jason 2026-08-15). Just
// two buckets — this isn't device detection, only the same >=720px breakpoint the
// CSS itself uses for the mobile/desktop column split (index.css).
function viewportBucket() {
  try { return window.innerWidth >= 720 ? 'wide' : 'mobile' } catch { return null }
}

// A dev server is not a user. Nov 2 2026: 90 of 142 "visitors" on Admin → Growth were the
// nightly walks and headless probes against localhost logging into PRODUCTION analytics.
// ref=e2e tagging only covered the runs that remembered to tag. Pure for the node test;
// `override` ('1' in localStorage nextep_log_local) lets a local session opt back in.
export function isLocalDev(hostname, override) {
  if (override === '1') return false
  const h = String(hostname || '').toLowerCase()
  return h === 'localhost' || h === '127.0.0.1' || h === '[::1]' || h.endsWith('.localhost')
}
let localOverride = null
const skipLocal = () => {
  try { if (localOverride == null) localOverride = localStorage.getItem('nextep_log_local') } catch { localOverride = '' }
  try { return isLocalDev(window.location.hostname, localOverride) } catch { return false }
}

export function logEvent(event, props = {}) {
  try {
    if (skipLocal()) return
    // Merge first-touch source into every event; explicit props still win on collision.
    const vid = visitorId()
    const vp  = viewportBucket()
    const at  = getAttribution()
    const merged = { ref: at.ref, ...(at.camp ? { camp: at.camp } : {}), ...(vid ? { vid } : {}), ...(vp ? { vp } : {}), ...(props || {}) }
    // Fire-and-forget: no await, no error surfacing. rpc() returns a thenable.
    supabase.rpc('log_event', { p_event: event, p_props: merged }).then(() => {}, () => {})
  } catch { /* analytics must never break a user flow */ }
}

// Log a follow, and — the first time this device ever follows anyone — also emit the
// funnel's `first_follow` step. Centralized here so the scattered follow-button sites
// just call logFollow(source); the localStorage guard mirrors the app's existing
// `nextep_first_follow_celebrated` first-follow detection.
export function logFollow(source) {
  logEvent('follow', { source })
  try {
    if (!localStorage.getItem('nextep_first_follow_logged')) {
      localStorage.setItem('nextep_first_follow_logged', '1')
      logEvent('first_follow', { source })
    }
  } catch { /* ignore storage failures */ }
}
